import React, { useMemo, useState } from 'react';
import { Input, Modal, Tabs, Tree } from 'antd';
import {
  BACKEND_PERMISSION_TREE,
  FRONTEND_PERMISSION_TREE,
  LEGACY_PERMISSION_TREE,
  buildAntdTreeData,
  buildOtherTreeData,
  collectCatalogPermissionNames,
  permissionIdsToTreeKeys,
  treeKeysToPermissionIds
} from './permissionTreeCatalog';
import { mergeGroupPermissionSelection } from './groupPermissionSelection';

/**
 * 分组权限授权弹窗：前台 / 后台两大页 + 树形勾选（按菜单顺序）。
 */
export default function GroupPermissionAuthModal({
  open,
  groupName,
  permissions = [],
  selectedPermissionIds = [],
  savingMsg = '',
  onCancel,
  onOk,
  onChangeSelectedIds
}) {
  const [activeTab, setActiveTab] = useState('frontend');
  const [keyword, setKeyword] = useState('');

  const permByName = useMemo(() => {
    const map = new Map();
    (permissions || []).forEach((p) => {
      if (p?.name) map.set(p.name, p);
    });
    return map;
  }, [permissions]);

  const catalogNames = useMemo(() => collectCatalogPermissionNames(), []);

  const frontendTree = useMemo(
    () => [
      ...buildAntdTreeData(FRONTEND_PERMISSION_TREE, permByName),
      ...buildAntdTreeData(LEGACY_PERMISSION_TREE, permByName)
    ],
    [permByName]
  );
  const backendTree = useMemo(
    () => buildAntdTreeData(BACKEND_PERMISSION_TREE, permByName),
    [permByName]
  );
  const otherTree = useMemo(
    () => buildOtherTreeData(permissions, catalogNames),
    [permissions, catalogNames]
  );

  const checkedKeys = useMemo(
    () => permissionIdsToTreeKeys(selectedPermissionIds),
    [selectedPermissionIds]
  );

  const filterTree = (nodes, kw) => {
    if (!kw) return nodes;
    const lower = kw.toLowerCase();
    const walk = (list) => {
      const out = [];
      (list || []).forEach((node) => {
        const titleText = String(node.title || '');
        const selfHit = titleText.toLowerCase().includes(lower);
        const children = walk(node.children || []);
        if (selfHit || children.length) {
          out.push({
            ...node,
            children: children.length ? children : node.children
          });
        }
      });
      return out;
    };
    return walk(nodes);
  };

  const frontendFiltered = useMemo(
    () => filterTree(frontendTree, keyword.trim()),
    [frontendTree, keyword]
  );
  const backendFiltered = useMemo(
    () => filterTree(
      otherTree.length ? [...backendTree, ...otherTree] : backendTree,
      keyword.trim()
    ),
    [backendTree, otherTree, keyword]
  );

  const handleCheck = (keys) => {
    // 前台/后台分 Tab，且搜索会过滤树：antd onCheck 只带当前树可见节点。
    // 必须保留「当前树之外」已选权限，否则保存时另一 Tab 的授权会被冲掉。
    const activeTree = activeTab === 'frontend' ? frontendFiltered : backendFiltered;
    const merged = mergeGroupPermissionSelection(
      selectedPermissionIds,
      activeTree,
      keys,
      treeKeysToPermissionIds
    );
    onChangeSelectedIds?.(merged);
  };

  const selectedCount = selectedPermissionIds.length;

  return (
    <Modal
      open={open}
      title={groupName ? `分组权限授权 — ${groupName}` : '分组权限授权'}
      onCancel={onCancel}
      onOk={onOk}
      okText="保存授权"
      cancelText="取消"
      width={860}
      destroyOnHidden
      styles={{ body: { paddingTop: 12, maxHeight: '70vh', overflow: 'auto' } }}
    >
      <div style={{ marginBottom: 12, color: '#666', fontSize: 13 }}>
        前台按导航编排（指挥系统可细分画板/轨迹等）；后台按管理菜单编排。
        「历史兼容」为旧访客权限，建议迁移到新页面权限后删除。
        勾选父级可批量选中下级；当前已选{' '}
        <strong style={{ color: '#1677ff' }}>{selectedCount}</strong> 项。
        修改后用户需重新登录后生效。
      </div>

      <Input.Search
        allowClear
        placeholder="搜索权限点名称或描述"
        value={keyword}
        onChange={(e) => setKeyword(e.target.value)}
        style={{ marginBottom: 12, maxWidth: 360 }}
      />

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: 'frontend',
            label: `前台页面（${countPermNodes(frontendFiltered)}）`,
            children: (
              <TreePanel
                remountKey={`fe-${keyword}`}
                treeData={frontendFiltered}
                checkedKeys={checkedKeys}
                onCheck={handleCheck}
                emptyText="暂无匹配的前台权限点"
              />
            )
          },
          {
            key: 'backend',
            label: `后台页面（${countPermNodes(backendFiltered)}）`,
            children: (
              <TreePanel
                remountKey={`be-${keyword}`}
                treeData={backendFiltered}
                checkedKeys={checkedKeys}
                onCheck={handleCheck}
                emptyText="暂无匹配的后台权限点"
              />
            )
          }
        ]}
      />

      {savingMsg ? (
        <div style={{
          marginTop: 12,
          color: savingMsg.includes('成功') ? '#389e0d' : '#cf1322'
        }}
        >
          {savingMsg}
        </div>
      ) : null}
    </Modal>
  );
}

function countPermNodes(nodes) {
  let n = 0;
  const walk = (list) => {
    (list || []).forEach((node) => {
      if (String(node.key || '').startsWith('perm:')) n += 1;
      if (node.children?.length) walk(node.children);
    });
  };
  walk(nodes);
  return n;
}

function TreePanel({ remountKey, treeData, checkedKeys, onCheck, emptyText }) {
  if (!treeData?.length) {
    return <div style={{ color: '#999', padding: '24px 0' }}>{emptyText}</div>;
  }
  return (
    <div
      style={{
        border: '1px solid #f0f0f0',
        borderRadius: 8,
        padding: '8px 12px',
        background: '#fafafa',
        minHeight: 320
      }}
    >
      <Tree
        key={remountKey || 'tree'}
        checkable
        defaultExpandAll
        selectable={false}
        treeData={treeData}
        checkedKeys={checkedKeys}
        onCheck={onCheck}
        style={{ background: 'transparent' }}
      />
    </div>
  );
}
